// Setup › Finance › Prices (`/c/{slug}/setup/finance/prices`; 11 §Price lists, M3-04
// `priceLists.*`): what a model's tokens, a sandbox or compute minute, a tool call, storage and
// media cost. Platform and agency rows arrive mirrored from the control plane; a company row
// beats them from its date (a model's row beats the all-models row; the latest date wins). Every
// step and sandbox stop is priced when it is recorded, so a new row prices work from its date on,
// never backwards. Managers. One call on load (the prices, filtered by unit).
import { newIdempotencyKey } from "@companyos/sdk";
import {
  Badge,
  Button,
  EmptyState,
  Input,
  NativeSelect,
  NativeSelectOption,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@companyos/ui";
import { useInfiniteQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { PlusIcon, TagIcon } from "lucide-react";
import { useId, useState } from "react";
import type { Api } from "../../api/client";
import type { CompanyAccess } from "../company";
import { formatDate } from "../format";
import { LoadError, Loading, LoadMore } from "../screen-parts";
import { type Currency, CurrencySelect, FinancePage, FormDialog, FormField } from "./finance-parts";
import { financeKeys, pricesQuery } from "./finance-queries";

type Price = Awaited<ReturnType<Api["client"]["priceLists"]["list"]>>["data"][number];
type Unit = Price["unit"];

export const UNIT_LABELS: Record<Unit, string> = {
  token_in: "Input token",
  token_out: "Output token",
  cache_read: "Cached token read",
  sandbox_minute: "Sandbox minute",
  compute_minute: "Compute minute",
  tool_call: "Tool call",
  storage_gb_month: "GB of storage a month",
  media_minute: "Media minute",
};

const SCOPE_TONE = { company: "accent", agency: "info", platform: "neutral" } as const;

/** Micros (millionths of the currency unit) as a price: "$0.000003", "$0.02". */
export function microsText(micros: number, currency: string): string {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 6,
  }).format(micros / 1_000_000);
}

/** "0.000003" (currency units) → micros; null when not a non-negative number with ≤ 6 decimals. */
export function toMicros(text: string): number | null {
  const v = text.trim();
  if (!/^\d+(\.\d{1,6})?$/.test(v)) return null;
  const [whole = "0", frac = ""] = v.split(".");
  return Number(whole) * 1_000_000 + Number(frac.padEnd(6, "0"));
}

export function PricesScreen({ api, company }: { api: Api; company: CompanyAccess }) {
  const [unit, setUnit] = useState<Unit | "">("");
  const [adding, setAdding] = useState(false);
  const prices = useInfiniteQuery(pricesQuery(api, company.id, unit));
  const rows = prices.data?.pages.flatMap((p) => p.data) ?? [];
  return (
    <FinancePage
      company={company}
      where="setup"
      title="Prices"
      icon={TagIcon}
      actions={
        <Button size="sm" onClick={() => setAdding(true)}>
          <PlusIcon aria-hidden="true" />
          Add a price
        </Button>
      }
    >
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <NativeSelect
          aria-label="Unit"
          value={unit}
          onChange={(e) => setUnit(e.target.value as Unit | "")}
        >
          <NativeSelectOption value="">Every unit</NativeSelectOption>
          {(Object.keys(UNIT_LABELS) as Unit[]).map((u) => (
            <NativeSelectOption key={u} value={u}>
              {UNIT_LABELS[u]}
            </NativeSelectOption>
          ))}
        </NativeSelect>
      </div>
      {prices.isPending ? (
        <Loading label="Loading prices" rows={4} />
      ) : prices.isError ? (
        <LoadError what="Prices" error={prices.error} />
      ) : rows.length === 0 ? (
        <EmptyState
          variant="card"
          icon={TagIcon}
          title="No prices yet"
          description="Platform prices arrive from the control plane. Add a company price to set your own."
        />
      ) : (
        <div className="flex min-w-0 flex-col gap-3">
          <Table data-slot="prices">
            <TableCaption>
              Company rows win over agency and platform rows; within a scope the latest date wins.
            </TableCaption>
            <TableHeader>
              <TableRow>
                <TableHead scope="col">Unit</TableHead>
                <TableHead scope="col">Model</TableHead>
                <TableHead scope="col">Set by</TableHead>
                <TableHead scope="col" className="text-right">
                  Price
                </TableHead>
                <TableHead scope="col" className="max-sm:hidden">
                  From
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id} data-price={r.id} data-scope={r.scope}>
                  <TableHead scope="row" className="font-normal">
                    {UNIT_LABELS[r.unit]}
                  </TableHead>
                  <TableCell className="text-muted-foreground">
                    {r.model ?? "Every model"}
                  </TableCell>
                  <TableCell>
                    <Badge variant={SCOPE_TONE[r.scope]} className="capitalize">
                      {r.scope}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-right tabular-nums">
                    {microsText(r.price_micros, r.currency)}
                    {r.per_units > 1 && (
                      <span className="text-muted-foreground">
                        {" "}
                        / {r.per_units === 1_000_000 ? "1M" : r.per_units.toLocaleString()}
                      </span>
                    )}
                  </TableCell>
                  <TableCell className="text-muted-foreground max-sm:hidden">
                    {formatDate(r.effective_from)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
          <LoadMore
            what="prices"
            hasMore={prices.hasNextPage}
            loading={prices.isFetchingNextPage}
            onMore={() => void prices.fetchNextPage()}
          />
        </div>
      )}
      {adding ? (
        <AddPriceDialog api={api} company={company} onClose={() => setAdding(false)} />
      ) : null}
    </FinancePage>
  );
}

function AddPriceDialog({
  api,
  company,
  onClose,
}: {
  api: Api;
  company: CompanyAccess;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [unit, setUnit] = useState<Unit>("token_in");
  const [model, setModel] = useState("");
  const [price, setPrice] = useState("");
  const [currency, setCurrency] = useState<Currency>("USD");
  const [from, setFrom] = useState(() => new Date().toISOString().slice(0, 10));
  const ids = { unit: useId(), model: useId(), price: useId(), cur: useId(), from: useId() };
  const micros = toMicros(price);
  const add = useMutation({
    mutationFn: () =>
      api.client.priceLists.create({
        params: { co: company.id },
        idempotencyKey: newIdempotencyKey(),
        body: {
          unit,
          model: model.trim() || null,
          price_micros: micros ?? 0,
          currency,
          effective_from: from,
        },
      }),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: financeKeys.all(company.id) });
      onClose();
    },
  });
  return (
    <FormDialog
      open
      onOpenChange={(o) => (o ? null : onClose())}
      title="Add a company price"
      description="It prices work recorded from its date on; earlier work keeps its price."
      submitLabel="Add price"
      pending={add.isPending}
      error={add.error}
      canSubmit={micros !== null && Boolean(from)}
      onSubmit={() => add.mutate()}
    >
      <div className="grid gap-4 sm:grid-cols-2">
        <FormField id={ids.unit} label="Unit">
          <NativeSelect
            id={ids.unit}
            value={unit}
            onChange={(e) => setUnit(e.target.value as Unit)}
          >
            {(Object.keys(UNIT_LABELS) as Unit[]).map((u) => (
              <NativeSelectOption key={u} value={u}>
                {UNIT_LABELS[u]}
              </NativeSelectOption>
            ))}
          </NativeSelect>
        </FormField>
        <FormField id={ids.model} label="Model" hint="Empty: every model without its own row.">
          <Input
            id={ids.model}
            maxLength={64}
            value={model}
            onChange={(e) => setModel(e.target.value)}
          />
        </FormField>
        <FormField
          id={ids.price}
          label="Price per unit"
          error={price !== "" && micros === null ? "Up to 6 decimals, like 0.000003." : null}
        >
          <Input
            id={ids.price}
            inputMode="decimal"
            required
            value={price}
            onChange={(e) => setPrice(e.target.value)}
          />
        </FormField>
        <FormField id={ids.cur} label="Currency">
          <CurrencySelect id={ids.cur} value={currency} onChange={setCurrency} />
        </FormField>
        <FormField id={ids.from} label="From">
          <Input
            id={ids.from}
            type="date"
            required
            value={from}
            onChange={(e) => setFrom(e.target.value)}
          />
        </FormField>
      </div>
    </FormDialog>
  );
}
